import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { activeStatus } from "consts";
import { BaseProviderType, PermissionProviderType, UserModel } from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as UserService from "services/user";
import { equalObjects, sortBy } from "utils";

type FilterType = {
  name: string;
  accessLogin: string;
  supervisor: number | string;
  projects: number[];
  status: 0 | 1 | -1;
};

type UserContextType = {
  loading: boolean;
  filter: FilterType;
  hasFilter: boolean;
  users?: UserModel[];
  userList?: UserModel[];
  activeUsers?: UserModel[];
  getUserList: () => Promise<void>;
  getUsers: () => Promise<void>;
  onChangeFilter: (filter: keyof FilterType, value: unknown) => void;
  onResetFilter: () => void;
};

const UserContext = createContext({} as UserContextType);

const initialFilter: FilterType = {
  name: "",
  accessLogin: "",
  supervisor: "",
  projects: [],
  status: 1,
};

const Provider = ({ children }: BaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>({ ...initialFilter });
  const [userList, setUserList] = useState<UserModel[]>();
  const [users, setUsers] = useState<UserModel[]>();
  const { pIdProject } = useProfile();
  const { error, warning, info } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasFilter = useMemo(
    () => !equalObjects(initialFilter, filter),
    [filter],
  );

  const activeUsers = useMemo(() => users?.filter((u) => u.IsActive), [users]);

  const getUserList = useCallback(async () => {
    setLoading(true);
    let status: boolean | undefined;
    if (filter.status) status = filter.status === activeStatus;

    if (filter.projects?.length)
      await UserService.getUsers({
        pNmUser: filter.name,
        pIdProject: filter.projects,
        pIdUserParent: filter.supervisor as number,
        pIsActive: status,
        pDeLogin: filter.accessLogin,
      })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0) setUserList(data.userList);
          else warning({ description: t("errors.failedToFetchList") });
        })
        .catch(errorsResolver.defaultError);
    else info({ description: t("alerts.selectProjects") });
    setLoading(false);
  }, [filter, errorsResolver, warning, info, t]);

  const getUsers = useCallback(async () => {
    setLoading(true);
    if (pIdProject)
      await UserService.getUsers({
        pIdProject: [pIdProject],
      })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0) setUsers(sortBy(data.userList, "NmUser"));
          else warning({ description: t("errors.failedToFetchList") });
        })
        .catch(errorsResolver.defaultError);
    setLoading(false);
  }, [pIdProject, errorsResolver, warning, t]);

  const onChangeFilter = useCallback(
    (filter: keyof FilterType, value: unknown) => {
      setFilter((current) => ({ ...current, [filter]: value }));
    },
    [],
  );

  const onResetFilter = useCallback(() => {
    setFilter({ ...initialFilter });
    setUserList(undefined);
  }, []);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  return (
    <UserContext.Provider
      value={{
        loading,
        filter,
        hasFilter,
        userList,
        users,
        activeUsers,
        getUserList,
        getUsers,
        onChangeFilter,
        onResetFilter,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useUser = () => useContext(UserContext);
